import * as t from "io-ts";
import { UUID as uuidCodec } from "io-ts-types/UUID";

import { currencyCodec } from "../codecs/currency";
import {
  userResponseCodec,
  userFeedResponseCodec,
  userFollowResponseCodec,
  followInfoResponseCodec,
  feedItemResponseCodec,
  nonprofitResponseCodec,
  nonprofitFeedResponseCodec,
  tagResponseCodec,
  userTagResponseCodec,
  fundraiserCodec,
  fundraiserFeedCodec,
} from "../codecs/entities";
import { safeIntCodec } from "../codecs/number";
import { usernameCodec } from "../codecs/username";
import { HttpMethod } from "../helpers/http";

import {
  makeRouteSpec,
  listParamsCodec,
  listResponseCodec,
  makeCachedRouteSpec,
} from ".";

const userCountsCodec = t.union([
  t.type({}),
  t.type({
    fundCount: t.number,
    fundraiserCount: t.number,
    giftCount: t.number,
    likeCount: t.number,
    joinCount: t.number,
  }),
]);
const userTotalDonatedCodec = t.partial({
  totalDonated: t.type({
    amountInMinDenom: safeIntCodec,
    currency: currencyCodec,
  }),
});
const getUserParamsCodec = t.type({});
const getUserBodyCodec = t.type({});
export const getUserResponseBodyCodec = t.intersection([
  t.type({
    user: userResponseCodec,
    followInfo: followInfoResponseCodec,
  }),
  userCountsCodec,
  userTotalDonatedCodec,
  t.type({ isOwner: t.boolean }),
]);
export const getUserRouteSpec = makeRouteSpec({
  path: "/users/:identifier",
  method: HttpMethod.GET,
  authenticated: false,
  tokensCodec: t.type({ identifier: t.union([uuidCodec, usernameCodec]) }),
  paramsCodec: getUserParamsCodec,
  bodyCodec: getUserBodyCodec,
  responseBodyCodec: getUserResponseBodyCodec,
  publicRoute: {
    publicCacheLengthMinutes: 1,
  },
});

export type UserProfileResponse = t.TypeOf<typeof getUserResponseBodyCodec>;

const getUserFundsParamsCodec = listParamsCodec;
const getUserFundsBodyCodec = t.type({});
const getUserFundsResponseBodyCodec = t.intersection([
  t.type({
    items: t.array(feedItemResponseCodec),
    users: t.array(userResponseCodec),
    nonprofits: t.array(nonprofitResponseCodec),
    fundraisers: t.array(fundraiserCodec),
  }),
  // TODO: move the tags to the type above after deploying
  t.partial({
    tags: t.array(tagResponseCodec),
  }),
  listResponseCodec,
]);
export const getUserFundsRouteSpec = makeCachedRouteSpec({
  path: "/users/:id/lists",
  method: HttpMethod.GET,
  version: 0,
  tokensCodec: t.type({ id: t.union([uuidCodec, usernameCodec]) }),
  paramsCodec: getUserFundsParamsCodec,
  bodyCodec: getUserFundsBodyCodec,
  responseBodyCodec: getUserFundsResponseBodyCodec,
});

const getUserFeedParamsCodec = listParamsCodec;
const getUserFeedBodyCodec = t.type({});
const getUserFeedResponseBodyCodec = t.intersection([
  t.type({
    items: t.array(feedItemResponseCodec),
    users: t.array(userFeedResponseCodec),
    nonprofits: t.array(nonprofitFeedResponseCodec),
    nonprofitTags: t.array(tagResponseCodec),
    fundraisers: t.array(fundraiserFeedCodec),
  }),
  listResponseCodec,
]);
export const getUserFeedRouteSpec = makeCachedRouteSpec({
  path: "/users/:id/feed",
  method: HttpMethod.GET,
  version: 0,
  tokensCodec: t.type({ id: t.union([uuidCodec, usernameCodec]) }),
  paramsCodec: getUserFeedParamsCodec,
  bodyCodec: getUserFeedBodyCodec,
  responseBodyCodec: getUserFeedResponseBodyCodec,
});

export const getUserJoinsFeedRouteSpec = makeCachedRouteSpec({
  path: "/users/:id/joins",
  method: HttpMethod.GET,
  version: 0,
  tokensCodec: t.type({ id: t.union([uuidCodec, usernameCodec]) }),
  paramsCodec: getUserFeedParamsCodec,
  bodyCodec: getUserFeedBodyCodec,
  responseBodyCodec: getUserFeedResponseBodyCodec,
});

const getUserLikesParamsCodec = listParamsCodec;
const getUserLikesBodyCodec = t.type({});
const getUserLikesResponseBodyCodec = t.intersection([
  t.type({
    items: t.array(feedItemResponseCodec),
    users: t.array(userResponseCodec),
    nonprofits: t.array(nonprofitResponseCodec),
    nonprofitTags: t.array(tagResponseCodec),
    fundraisers: t.array(fundraiserCodec),
  }),
  listResponseCodec,
]);
export const getUserLikesRouteSpec = makeCachedRouteSpec({
  path: "/users/:id/likes",
  method: HttpMethod.GET,
  version: 0,
  tokensCodec: t.type({ id: t.union([uuidCodec, usernameCodec]) }),
  paramsCodec: getUserLikesParamsCodec,
  bodyCodec: getUserLikesBodyCodec,
  responseBodyCodec: getUserLikesResponseBodyCodec,
});

const getUserCausesParamsCodec = listParamsCodec;
const getUserCausesBodyCodec = t.type({});
const getUserCausesResponseBodyCodec = t.intersection([
  t.type({
    nonprofitTags: t.array(tagResponseCodec),
    tags: t.array(userTagResponseCodec),
  }),
  listResponseCodec,
]);
export const getUserCausesRouteSpec = makeCachedRouteSpec({
  path: "/users/:id/causes",
  method: HttpMethod.GET,
  version: 0,
  tokensCodec: t.type({ id: uuidCodec }),
  paramsCodec: getUserCausesParamsCodec,
  bodyCodec: getUserCausesBodyCodec,
  responseBodyCodec: getUserCausesResponseBodyCodec,
});

const getUserFollowsParamsCodec = t.type({});
const getUserFollowsBodyCodec = t.type({});
const getUserFollowsResponseBodyCodec = t.type({
  following: t.array(userFollowResponseCodec),
  followers: t.array(userFollowResponseCodec),
  users: t.array(userResponseCodec),
});
export const getUserFollowsRouteSpec = makeCachedRouteSpec({
  path: "/users/:id/follows",
  method: HttpMethod.GET,
  version: 0,
  tokensCodec: t.type({ id: uuidCodec }),
  paramsCodec: getUserFollowsParamsCodec,
  bodyCodec: getUserFollowsBodyCodec,
  responseBodyCodec: getUserFollowsResponseBodyCodec,
});
